import { supabase } from './supabase';
import { PROMPT_VERSION } from '../utils/appVersion';
import { registrarActividadDiaria } from './rachaDiaria';
import { NANDA_DB } from '../data/nanda';
import { esModeloValoracion, modeloValoracion } from './paeModelos';

// ─── Resumen clínico para observabilidad (pae_outputs) ─────────────────────
// Extracción DEFENSIVA de los códigos del PAE generado. Best-effort: si la
// forma no calza (cambió el modelo de datos), devuelve lo que pudo sin lanzar,
// para no escribir datos erróneos ni romper el guardado. No captura texto libre.
function _pushCodes(arr, target) {
  for (const x of Array.isArray(arr) ? arr : []) {
    const c = typeof x === 'string' ? x : x?.code;
    if (c) target.add(c);
  }
}

// Solo objetos con `code`: en el plan del Rápido, `nic` es la lista plana de
// FRASES de actividades (compat), no de códigos, y antes esas frases se
// guardaban como si fueran códigos NIC (auditoría del 4-oct-2026).
function _pushCodigosDeObjetos(arr, target) {
  for (const x of Array.isArray(arr) ? arr : []) {
    if (x && typeof x === 'object' && x.code) target.add(String(x.code));
  }
}

export function extraerResumenClinico({ modo, formData, selected }) {
  const nanda = new Set(), noc = new Set(), nic = new Set();
  let servicio = null, dxMedico = null;
  try {
    if (modo === 'rapido') {
      const d = formData?.rapido || {};
      servicio = d.servicio || null;
      dxMedico = d.dx_medico || null;
      // La forma real del plan la arma generarPlan (PAERapido.jsx): `code`,
      // `nocCode`, `nicsBase` y `nicsEnriquecidas` con sus códigos. `{ raw }`
      // (sin plan local) no es un diagnóstico.
      const entries = d._plan && typeof d._plan === 'object' ? Object.values(d._plan) : [];
      for (const e of entries) {
        if (!e || typeof e !== 'object') continue;
        if (e.code) nanda.add(e.code);
        if (e.nocCode) noc.add(String(e.nocCode));
        _pushCodigosDeObjetos(e.noc, noc);
        _pushCodigosDeObjetos(e.nicsBase, nic);
        _pushCodigosDeObjetos(e.nicsEnriquecidas, nic);
        _pushCodigosDeObjetos(e.nics, nic);
        _pushCodigosDeObjetos(e.nic, nic);
      }
    } else {
      const a = formData?.anamnesis || {};
      servicio = a.servicio || null;
      dxMedico = a.dx_medico || null;
      // El Detallado guarda `selected` como CÓDIGOS ("00032"), no como
      // objetos: leer solo `diag.code` dejaba dx_nanda_codes siempre vacío.
      for (const diag of Array.isArray(selected) ? selected : []) {
        const code = typeof diag === 'string' ? diag : diag?.code;
        if (code) nanda.add(code);
        if (diag && typeof diag === 'object') {
          _pushCodes(diag.noc, noc);
          _pushCodes(diag.nic, nic);
        }
      }
    }
  } catch { /* best-effort */ }
  return { servicio, dxMedico, nanda: [...nanda], noc: [...noc], nic: [...nic] };
}

// Registra una fila-resumen en pae_outputs. Fire-and-forget: cualquier fallo se
// traga (RLS insert-own; el guardado del PAE es lo que importa).
async function registrarPaeOutput({ paeId, userId, modo, formData, selected }) {
  try {
    const r = extraerResumenClinico({ modo, formData, selected });
    await supabase.from('pae_outputs').insert({
      pae_id:              paeId,
      user_id:             userId,
      modulo:              modo === 'rapido' ? 'pae_rapido' : 'pae_detallado',
      dx_medico_ingresado: r.dxMedico,
      servicio:            r.servicio,
      dx_nanda_codes:      r.nanda,
      noc_codes:           r.noc,
      nic_codes:           r.nic,
      prompt_version:      PROMPT_VERSION,
    });
  } catch { /* observabilidad best-effort */ }
}

// ─── Extraer título legible del formData ───────────────────────────────────
function extraerTitulo(formData, modo) {
  try {
    if (modo === 'rapido') {
      const d = formData?.rapido;
      const nombre = d?.iniciales || '';
      const edad = d?.edad ? `${d.edad}a` : '';
      const dx = d?.dx_medico || '';
      return [nombre, edad, dx].filter(Boolean).join(' · ') || 'PAE Rápido';
    } else {
      const a = formData?.anamnesis;
      const nombre = a?.iniciales || '';
      const edad = a?.edad ? `${a.edad}a` : '';
      const dx = a?.dx_medico || '';
      return [nombre, edad, dx].filter(Boolean).join(' · ') || 'PAE Detallado';
    }
  } catch {
    return modo === 'rapido' ? 'PAE Rápido' : 'PAE Detallado';
  }
}

// ─── Guardar PAE ───────────────────────────────────────────────────────────
export async function guardarPAE({ userId, modo, formData, selected = [], anamnesis = null }) {
  if (!userId) return null;
  const titulo = extraerTitulo(formData, modo);
  const base = modo === 'rapido' ? { formData } : { formData, selected };
  const datos = anamnesis ? { ...base, anamnesis } : base;

  const { data, error } = await supabase
    .from('paes')
    .insert({ user_id: userId, modo, estado: 'completado', titulo, datos })
    .select('id')
    .single();

  if (error) console.error('Error guardando PAE:', error);

  // Observabilidad: fila-resumen en pae_outputs (fire-and-forget, no bloquea).
  if (data?.id) {
    registrarPaeOutput({ paeId: data.id, userId, modo, formData, selected }).catch(() => {});
    // Gancho diario: guardar un PAE cuenta como actividad del día.
    registrarActividadDiaria(userId).catch(() => {});
  }

  return data?.id || null;
}

// ─── Listar PAEs del usuario ───────────────────────────────────────────────
// Además de la fila, el modelo de enfermería con que se valoró (texto de
// catálogo leído del jsonb, sin traer `datos` entero): "Tus PAEs" lo muestra
// en la fila. Si esa lectura fallara, se reintenta la lista de siempre: el
// modelo es un extra, la lista no puede caerse por él.
const COLUMNAS_LISTA = 'id, modo, estado, titulo, created_at';
const COLUMNAS_LISTA_CON_MODELO = `${COLUMNAS_LISTA}, modelo_rapido:datos->formData->rapido->>modelo, modelo_sintesis:datos->formData->sintesisModelo->>modelo`;

function consultarLista(userId, limit, columnas) {
  return supabase
    .from('paes')
    .select(columnas)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
}

function filaLista(row) {
  const { modelo_rapido, modelo_sintesis, ...resto } = row || {};
  const modelo = resto.modo === 'rapido' ? modelo_rapido : modelo_sintesis;
  return { ...resto, modelo: typeof modelo === 'string' && modelo ? modelo : null };
}

// Distingue "no tienes PAEs" de "no pudimos cargarlos" (`ok: false`), para
// que la pantalla ofrezca Reintentar en vez de decir "0 PAEs guardados."
export async function cargarListaPAEs(userId, limit = 5) {
  if (!userId) return { ok: true, paes: [] };
  try {
    let { data, error } = await consultarLista(userId, limit, COLUMNAS_LISTA_CON_MODELO);
    if (error) ({ data, error } = await consultarLista(userId, limit, COLUMNAS_LISTA));
    if (error) { console.error('Error listando PAEs:', error); return { ok: false, paes: [] }; }
    return { ok: true, paes: (data || []).map(filaLista) };
  } catch (e) {
    console.error('Error listando PAEs:', e);
    return { ok: false, paes: [] };
  }
}

// Contrato de siempre para quien solo necesita la lista (menú, buscador,
// Benito, Mi perfil): ante un error, lista vacía.
export async function listarPAEs(userId, limit = 5) {
  const { paes } = await cargarListaPAEs(userId, limit);
  return paes;
}

// ─── Lo que Benito puede saber de un PAE ───────────────────────────────────
// SOLO catálogo: el modelo con que se valoró y los diagnósticos NANDA del plan
// (código y nombre del catálogo NANDA_DB). Jamás hallazgos, valoración libre,
// iniciales ni texto de la IA. Un código que no está en el catálogo no sale.
const NANDA_POR_CODIGO = new Map(NANDA_DB.map((dx) => [dx.code, dx]));

export function catalogoPae({ modo, datos } = {}) {
  const formData = datos?.formData || {};
  const { nanda } = extraerResumenClinico({ modo, formData, selected: datos?.selected });
  const diagnosticos = nanda
    .map((code) => NANDA_POR_CODIGO.get(code))
    .filter(Boolean)
    .map((dx) => ({ code: dx.code, label: dx.label }));
  let modelo = null;
  if (modo === 'rapido') {
    const crudo = formData.rapido?.modelo;
    // Sin modelo escrito, el Rápido se valoraba con Henderson (paeModelos).
    if (crudo === 'libre') modelo = 'libre';
    else if (formData.rapido) modelo = esModeloValoracion(crudo) ? crudo : 'henderson';
  } else {
    const crudo = formData.sintesisModelo?.modelo;
    if (esModeloValoracion(crudo)) modelo = crudo;
    else if (formData.henderson) modelo = 'henderson';
  }
  const modeloCorto = esModeloValoracion(modelo) ? modeloValoracion(modelo).corto : null;
  return { modelo, modeloCorto, diagnosticos };
}

export async function catalogoDePAE(paeId, userId) {
  if (!userId || !paeId) return null;
  try {
    const { data, error } = await supabase
      .from('paes')
      .select('modo, datos')
      .eq('id', paeId)
      .eq('user_id', userId)
      .single();
    if (error || !data) return null;
    return catalogoPae({ modo: data.modo, datos: data.datos });
  } catch {
    return null;
  }
}

// ─── Registrar evento de métrica ──────────────────────────────────────────
// Los eventos se almacenan en Supabase si la tabla existe; si falla, se ignora.
// El tracking principal es via registrarMetrica() → Supabase (lib/metrics.js)
export async function registrarEvento({ userId, tipo, paeId = null, metadatos = {} }) {
  if (!userId) return;
  const { error } = await supabase.from('eventos').insert({ user_id: userId, tipo, pae_id: paeId, metadata: metadatos });
  if (error) console.warn('[registrarEvento] ignorado:', error.message);
}
// ─── Eliminar PAE ─────────────────────────────────────────────────────────
export async function eliminarPAE(paeId, userId) {
  if (!userId || !paeId) return false;
  const { error } = await supabase
    .from('paes')
    .delete()
    .eq('id', paeId)
    .eq('user_id', userId);
  if (error) { console.error('Error eliminando PAE:', error); return false; }
  return true;
}

// ─── Actualizar anamnesis IA en PAE existente ─────────────────────────────
// Hace read-modify-write sobre datos JSONB para no pisar otras claves.
export async function actualizarAnamnesisIA(paeId, userId, anamnesisObj) {
  if (!userId || !paeId) return false;
  const { data: current, error: loadErr } = await supabase
    .from('paes')
    .select('datos')
    .eq('id', paeId)
    .eq('user_id', userId)
    .single();
  if (loadErr || !current) return false;
  const { error } = await supabase
    .from('paes')
    .update({ datos: { ...current.datos, anamnesis: anamnesisObj } })
    .eq('id', paeId)
    .eq('user_id', userId);
  if (error) console.error('Error actualizando anamnesis IA:', error);
  return !error;
}

// ─── Actualizar el formData de un PAE ya guardado ──────────────────────────
// Read-modify-write sobre datos JSONB (patrón de actualizarAnamnesisIA): solo
// reemplaza `formData` y no pisa la anamnesis ni la selección. Lo usa el
// resumen del Rápido cuando un reintento de plazos o actividades termina
// después de guardar, para que la fila no quede con el plan a medias.
export async function actualizarFormDataPAE(paeId, userId, formData) {
  if (!userId || !paeId || !formData) return false;
  try {
    const { data: current, error: loadErr } = await supabase
      .from('paes')
      .select('datos')
      .eq('id', paeId)
      .eq('user_id', userId)
      .single();
    if (loadErr || !current) return false;
    const { error } = await supabase
      .from('paes')
      .update({ datos: { ...current.datos, formData } })
      .eq('id', paeId)
      .eq('user_id', userId);
    if (error) console.error('Error actualizando PAE:', error);
    return !error;
  } catch {
    return false;
  }
}

// ─── Cargar PAE completo ───────────────────────────────────────────────────
export async function cargarPAE(paeId, userId) {
  if (!userId || !paeId) return null;
  const { data, error } = await supabase
    .from('paes')
    .select('*')
    .eq('id', paeId)
    .eq('user_id', userId)
    .single();

  if (error) { console.error('Error cargando PAE:', error); return null; }
  return data;
}